import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type { Ref } from 'vue'

export function useActiveSection(sectionIds: Ref<string[]>, ready?: Ref<boolean>) {
  const activeSection = ref(sectionIds.value[0] ?? '')

  let scroller: HTMLElement | null = null
  let sectionElements: Array<{ id: string; el: HTMLElement }> = []
  let rafId = 0
  let locked = false
  let lockTimeout: ReturnType<typeof setTimeout> | undefined
  let preferredSectionId: string | null = null

  const lock = (sectionId: string, duration = 600) => {
    preferredSectionId = sectionId
    activeSection.value = sectionId
    locked = true
    clearTimeout(lockTimeout)
    lockTimeout = setTimeout(() => {
      locked = false
      update()
    }, duration)
  }

  const update = () => {
    if (!scroller) return

    if (locked) {
      return
    }

    const headerHeight = parseInt(getComputedStyle(scroller).getPropertyValue('--header-height'), 10) || 0
    const scrollerRect = scroller.getBoundingClientRect()
    const visibleBoundary = scrollerRect.top + headerHeight + 1

    if (preferredSectionId) {
      const preferredSection = sectionElements.find((section) => section.id === preferredSectionId)
      const preferredRect = preferredSection?.el.getBoundingClientRect()
      const preferredSectionVisible = Boolean(
        preferredRect && preferredRect.bottom > visibleBoundary && preferredRect.top < scrollerRect.bottom,
      )

      if (preferredSectionVisible) {
        if (activeSection.value !== preferredSectionId) {
          activeSection.value = preferredSectionId
        }
        return
      }

      preferredSectionId = null
    }

    let current = sectionElements[0]?.id ?? sectionIds.value[0] ?? ''
    let foundContainingSection = false

    for (const section of sectionElements) {
      const rect = section.el.getBoundingClientRect()
      if (rect.top <= visibleBoundary && rect.bottom > visibleBoundary) {
        current = section.id
        foundContainingSection = true
        break
      }
    }

    if (!foundContainingSection) {
      for (const section of sectionElements) {
        if (section.el.getBoundingClientRect().top <= visibleBoundary) {
          current = section.id
        }
      }
    }

    if (current !== activeSection.value) {
      activeSection.value = current
    }
  }

  const onScroll = () => {
    cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(update)
  }

  const setup = () => {
    cleanup()

    sectionElements = sectionIds.value
      .map((id) => {
        const el = document.getElementById(id)
        return el ? { id, el } : null
      })
      .filter((section): section is { id: string; el: HTMLElement } => Boolean(section))

    const firstEl = sectionElements[0]?.el ?? null
    scroller = firstEl?.closest('main') ?? null

    if (scroller) {
      scroller.addEventListener('scroll', onScroll, { passive: true })
      update()
    }
  }

  const cleanup = () => {
    if (scroller) {
      scroller.removeEventListener('scroll', onScroll)
      scroller = null
    }
    clearTimeout(lockTimeout)
    lockTimeout = undefined
    locked = false
    preferredSectionId = null
    sectionElements = []
    cancelAnimationFrame(rafId)
  }

  onMounted(() => {
    if (!ready || ready.value) {
      setup()
    }
  })
  onBeforeUnmount(cleanup)

  if (ready) {
    watch(ready, (isReady) => {
      if (isReady) {
        nextTick(setup)
      } else {
        cleanup()
      }
    })
  }

  watch(sectionIds, () => {
    if (!ready || ready.value) {
      nextTick(setup)
    }
  })

  return { activeSection, lock }
}
